/**
 * NHTSA vPIC decode — free, and North America only.
 *
 * This is the whole of the decode story. There is no free equivalent for
 * European or Middle East vehicles, so **every other region uses manual entry**
 * and that path has to be fast enough not to feel like a punishment. A decode
 * that fails is not an error state; it is the normal state outside the US and
 * Canada.
 *
 * Fields are read by `VariableId`, not by `Variable`. The display names carry
 * spaces and punctuation ("Fuel Type - Primary", "Body Class") and are
 * presentation, not contract; the ids are the stable key. Several variables
 * also repeat — "Other Engine Info" appears five times on a real response — so
 * a name-keyed map would silently keep whichever came last.
 */

export const VPIC_BASE = 'https://vpic.nhtsa.dot.gov/api/vehicles';

const FIELD = {
  bodyClass: 5,
  cylinders: 9,
  displacementL: 13,
  fuelType: 24,
  make: 26,
  model: 28,
  modelYear: 29,
  trim: 38,
  vehicleType: 39,
  errorCode: 143,
  errorText: 191,
} as const;

export interface VpicResult { Variable: string; VariableId: number; Value: string | null }
export interface VpicResponse { Results?: VpicResult[] }

export interface DecodedVehicle {
  make: string;
  model: string;
  year: number | null;
  trim: string;
  bodyStyle: 'sedan' | 'pickup' | 'suv' | 'van' | 'other';
  /** Free text, shown but never used to price anything. */
  engine: string;
  /** vPIC's own verdict. '0' means it decoded clean. */
  errorCode: string;
  errorText: string;
  /** True when vPIC could name the vehicle, whatever else it complained about. */
  usable: boolean;
}

/** vPIC uses these to mean "we have no data", and they must not reach a field. */
const EMPTY = new Set(['', 'Not Applicable', 'Not Reported', 'null']);

function value(results: VpicResult[], id: number): string {
  const hit = results.find((r) => r.VariableId === id);
  const v = hit?.Value ?? '';
  return v === null || EMPTY.has(v) ? '' : v.trim();
}

/**
 * vPIC returns makes in capitals — FORD, not Ford. Printing that on a customer's
 * estimate looks like shouting.
 */
export function titleCase(s: string): string {
  return s.toLowerCase().replace(/(^|[\s/-])([a-z])/g, (_m, sep, c) => sep + c.toUpperCase());
}

/**
 * vPIC's body classes are a long taxonomy; the shot list only needs the shape
 * of the car. Anything unrecognised becomes `other`, which still gets a usable
 * shot list rather than no vehicle at all.
 */
export function toBodyStyle(bodyClass: string, vehicleType: string): DecodedVehicle['bodyStyle'] {
  const s = `${bodyClass} ${vehicleType}`.toLowerCase();
  if (s.includes('pickup')) return 'pickup';
  if (s.includes('van')) return 'van';
  if (/sport utility|suv|crossover|multipurpose/.test(s)) return 'suv';
  if (/sedan|saloon|coupe|hatchback|convertible|wagon/.test(s)) return 'sedan';
  return 'other';
}

export function mapVpicResponse(json: VpicResponse): DecodedVehicle {
  const results = json.Results ?? [];
  const make = titleCase(value(results, FIELD.make));
  const model = value(results, FIELD.model);
  const yearRaw = value(results, FIELD.modelYear);
  const year = /^\d{4}$/.test(yearRaw) ? Number(yearRaw) : null;

  const engine = [
    value(results, FIELD.displacementL) && `${value(results, FIELD.displacementL)}L`,
    value(results, FIELD.cylinders) && `${value(results, FIELD.cylinders)}-cyl`,
    value(results, FIELD.fuelType),
  ].filter(Boolean).join(' ');

  return {
    make,
    model,
    year,
    trim: value(results, FIELD.trim),
    bodyStyle: toBodyStyle(value(results, FIELD.bodyClass), value(results, FIELD.vehicleType)),
    engine,
    errorCode: value(results, FIELD.errorCode),
    errorText: value(results, FIELD.errorText),
    // A make and model is enough to start work. Insisting on error code '0'
    // would reject vehicles vPIC named perfectly well but grumbled about.
    usable: Boolean(make && model),
  };
}

export type DecodeOutcome =
  | { kind: 'decoded'; vehicle: DecodedVehicle }
  | { kind: 'not_found' }
  | { kind: 'offline' }
  | { kind: 'failed'; message: string };

/**
 * Ask vPIC about a VIN.
 *
 * Every failure resolves rather than throwing, because the caller's next move
 * is the same in all of them: show the manual form. A decode is a convenience,
 * never a gate.
 */
export async function decodeVin(
  vin: string,
  opts: { fetch?: typeof fetch; signal?: AbortSignal } = {},
): Promise<DecodeOutcome> {
  const call = opts.fetch ?? globalThis.fetch;
  try {
    const res = await call(
      `${VPIC_BASE}/decodevin/${encodeURIComponent(vin)}?format=json`,
      { signal: opts.signal },
    );
    if (!res.ok) return { kind: 'failed', message: `vPIC returned ${res.status}` };
    const vehicle = mapVpicResponse((await res.json()) as VpicResponse);
    return vehicle.usable ? { kind: 'decoded', vehicle } : { kind: 'not_found' };
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      return { kind: 'failed', message: 'Cancelled' };
    }
    // A workshop is the wrong place to be told "TypeError: Failed to fetch".
    return { kind: 'offline' };
  }
}
